import { Button } from "@/components/common/Button/Button"
import { Logo } from "@/components/common/Logo/Logo"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { OnboardingTooltip } from "@/components/feature/OnboardingTooltip/OnboardingTooltip"
import { Layout } from "@/components/layout"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { completeOnboarding } from "@/utils/onboarding"
import { IconDatabaseImport } from "@tabler/icons-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"

export const OnboardingPage: React.FC = () => {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const [showTour, setShowTour] = useState(false)
  const [tourCompleted, setTourCompleted] = useState(false)
  const tourStartTimeRef = useRef<number | null>(null)
  const hasTrackedStart = useRef(false)

  useEffect(() => {
    if (!hasTrackedStart.current) {
      trackEvent(AnalyticsEvent.ONBOARDING_STARTED, {
        is_new_user: true,
      })
      hasTrackedStart.current = true
    }
  }, [])

  const handleEndTour = () => {
    const tourDuration = tourStartTimeRef.current
      ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
      : 0
    trackEvent(AnalyticsEvent.ONBOARDING_TOUR_COMPLETED, {
      tour_duration_seconds: tourDuration,
      skipped: true,
    })

    setShowTour(false)
    setTourCompleted(true)
  }

  const handleCompleteTour = async () => {
    try {
      await completeOnboarding()

      // Track tour completed (finished)
      const tourDuration = tourStartTimeRef.current
        ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
        : 0
      trackEvent(AnalyticsEvent.ONBOARDING_TOUR_COMPLETED, {
        tour_duration_seconds: tourDuration,
        skipped: false,
      })

      setTourCompleted(true)
      setShowTour(false)
    } catch {
      toast.error(translate("USER_UPDATE_ERROR"))
    }
  }

  const handleStartTour = () => {
    tourStartTimeRef.current = Date.now()
    setShowTour(true)
  }

  return (
    <Layout defaultSidebarState="open">
      <OnboardingTooltip
        showTour={showTour}
        handleEndTour={handleEndTour}
        handleCompleteTour={handleCompleteTour}
      />
      {!tourCompleted ? (
        <div className="flex min-h-full flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center rounded-2xl bg-grey3 px-12 py-20">
            <div className="flex h-10 w-60 items-center justify-center">
              <Logo />
            </div>

            <Typography
              className="m-8 max-w-96 text-center"
              as="p"
              variant="titleM"
            >
              {translate("ONBOARDING/DESCRIPTION")}
            </Typography>
            <div className="flex flex-col space-y-4">
              <Button
                variantColor="yellow"
                title={translate("ONBOARDING/START_TOUR")}
                label={translate("ONBOARDING/START_TOUR")}
                onClick={handleStartTour}
                variantSize="medium"
                isFullWidth={true}
              />
              <Button
                variantColor="dark"
                title={translate("ONBOARDING/SKIP")}
                label={translate("ONBOARDING/SKIP")}
                onClick={handleCompleteTour}
                variantSize="medium"
                isFullWidth={true}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-full flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center rounded-2xl bg-grey3 px-12 py-20">
            <div className="flex h-10 w-60 items-center justify-center">
              <Logo />
            </div>
            <div className="py-10">
              <IconDatabaseImport size={40} stroke={1} />
            </div>
            <Typography
              className="max-w-96 text-center"
              as="p"
              variant="titleM"
            >
              {translate("ONBOARDING/DB/HEADLINE")}
            </Typography>
            <Typography
              className="max-w-96 py-6 text-center"
              as="p"
              variant="textS"
            >
              {translate("ONBOARDING/DB/DESCRIPTION")}
            </Typography>
            <div className="flex flex-col space-y-4">
              <Button
                variantColor="yellow"
                title={translate("ONBOARDING/DB/UPLOAD")}
                label={translate("ONBOARDING/DB/UPLOAD")}
                onClick={() => {
                  navigate("/knowledgebase")
                }}
                variantSize="medium"
                isFullWidth={true}
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
